// web-frontend/src/contexts/AppSettingsContext.jsx
/**
 * 🔧 AppSettingsContext - DSK 앱 설정 관리 시스템
 * 
 * 기능:
 * - 데이터 절약 모드 (이미지 품질 저하, 썸네일 사용)
 * - 글꼴 크기 (작게/보통/크게) 전역 적용
 * - localStorage 저장으로 설정 유지
 * - CSS 변수 자동 업데이트
 * 
 * 🔥 2025-12-22 생성
 * 🔥🔥🔥 2025-12-22 수정 v2: 스케일 범위 조정 (92% ~ 108%)
 * - !important 남용 제거
 * - html font-size 직접 변경 제거 (CSS 변수만 사용)
 * - 동적 스타일시트 로직 단순화
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import logger from '../shared/utils/logger';

// ====================================
// Storage Keys
// ====================================
const STORAGE_KEYS = {
  FONT_SIZE: 'app_font_size',
  DATA_SAVER: 'app_data_saver',
};

// ====================================
// Font Size 설정
// ====================================
export const FontSizes = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

// 🔥🔥🔥 [핵심 수정 v3] 스케일 대신 고정 px 값 사용
// 스케일 방식은 예측이 어려워서, 직접 px 값을 지정
const FONT_SIZE_PRESETS = {
  [FontSizes.SMALL]: {
    xs: 12,    // 유지 (최소값)
    sm: 13,    // 14 → 13
    body: 15,  // 16 → 15
    md: 15,
    lg: 17,    // 18 → 17
    xl: 19,    // 20 → 19
    xxl: 22,   // 24 → 22
    h1: 30,    // 32 → 30
    lineHeight: 1.5,
  },
  [FontSizes.MEDIUM]: {
    xs: 12,
    sm: 14,
    body: 16,
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
    h1: 32,
    lineHeight: 1.5,
  },
  [FontSizes.LARGE]: {
    xs: 13,    // 12 → 13
    sm: 15,    // 14 → 15
    body: 17,  // 16 → 17
    md: 17,
    lg: 19,    // 18 → 19
    xl: 21,    // 20 → 21
    xxl: 26,   // 24 → 26
    h1: 34,    // 32 → 34
    lineHeight: 1.55,
  },
};

// ====================================
// 🔥🔥🔥 CSS 변수 업데이트 함수 (고정 px 값 사용)
// ====================================
// 🔥🔥🔥 [핵심 수정 v4 / 2026-06-01] 글꼴 크기 '실제 전역 적용'
//   기존: CSS 변수(--font-*)만 변경 → 변수를 쓰는 소수 컴포넌트에만 반영(대부분 px 하드코딩이라 미적용).
//   수정: CSS 변수는 medium 으로 고정(이중 스케일 방지)하고, 실제 크기 변경은 body 의 전역 zoom 으로 적용
//        → 하드코딩 px 컴포넌트까지 앱 전체 글꼴/콘텐츠가 함께 확대·축소된다.
const FONT_SCALE = {
  [FontSizes.SMALL]: 0.9,
  [FontSizes.MEDIUM]: 1.0,
  [FontSizes.LARGE]: 1.12,
};

function updateCSSVariables(size) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  // CSS 변수는 medium 기준 고정 (변수 사용 컴포넌트가 zoom 으로 이중 확대되지 않도록)
  const preset = FONT_SIZE_PRESETS[FontSizes.MEDIUM];
  root.style.setProperty('--font-xs', `${preset.xs}px`);
  root.style.setProperty('--font-sm', `${preset.sm}px`);
  root.style.setProperty('--font-body', `${preset.body}px`);
  root.style.setProperty('--font-md', `${preset.md}px`);
  root.style.setProperty('--font-lg', `${preset.lg}px`);
  root.style.setProperty('--font-xl', `${preset.xl}px`);
  root.style.setProperty('--font-xxl', `${preset.xxl}px`);
  root.style.setProperty('--font-h1', `${preset.h1}px`);
  root.style.setProperty('--base-line-height', preset.lineHeight.toString());

  const scale = FONT_SCALE[size] ?? 1.0;
  root.style.setProperty('--font-scale', String(scale));

  // data 속성 / body 클래스 (CSS 선택자용)
  root.setAttribute('data-font-size', size);
  document.body.classList.remove('font-small', 'font-medium', 'font-large');
  document.body.classList.add(`font-${size}`);

  // 🔥 전역 적용: px 하드코딩 컴포넌트까지 실제 글꼴 크기 반영 (zoom)
  try { document.body.style.zoom = scale === 1.0 ? '' : String(scale); } catch (e) { /* zoom 미지원 폴백 무시 */ }

  logger.debug(`[AppSettings] 글꼴 크기 적용: ${size} (전역 zoom=${scale})`);
}

// ====================================
// Context 생성
// ====================================
const AppSettingsContext = createContext(null);

// ====================================
// AppSettingsProvider 컴포넌트
// ====================================
export const AppSettingsProvider = ({ children }) => {
  // 초기화 완료 여부 추적
  const isInitialized = useRef(false);
  
  // 저장된 설정 또는 기본값 사용
  const [fontSize, setFontSizeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEYS.FONT_SIZE);
      return saved && Object.values(FontSizes).includes(saved) ? saved : FontSizes.MEDIUM;
    }
    return FontSizes.MEDIUM;
  });
  
  const [dataSaver, setDataSaverState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.DATA_SAVER) === 'true';
    }
    return false;
  });

  // ====================================
  // 🔤 글꼴 크기 변경 핸들러
  // ====================================
  const setFontSize = useCallback((size) => {
    if (!Object.values(FontSizes).includes(size)) {
      logger.warn('[AppSettings] 지원하지 않는 폰트 크기:', size);
      return;
    }
    
    logger.debug(`[AppSettings] setFontSize 호출: ${size}`);
    setFontSizeState(size);
    localStorage.setItem(STORAGE_KEYS.FONT_SIZE, size);
    
    // CSS 변수 업데이트
    updateCSSVariables(size);
  }, []);

  // ====================================
  // 📶 데이터 절약 모드 변경 핸들러
  // ====================================
  const setDataSaver = useCallback((enabled) => {
    const value = Boolean(enabled);
    setDataSaverState(value);
    localStorage.setItem(STORAGE_KEYS.DATA_SAVER, value.toString());
    
    if (typeof document !== 'undefined') {
      if (value) {
        document.body.classList.add('data-saver-mode');
        document.documentElement.setAttribute('data-saver', 'true');
      } else {
        document.body.classList.remove('data-saver-mode');
        document.documentElement.setAttribute('data-saver', 'false');
      }
    }
    
    logger.debug(`[AppSettings] 데이터 절약 모드: ${value ? 'ON' : 'OFF'}`);
  }, []);

  // ====================================
  // 🔄 데이터 절약 모드 토글
  // ====================================
  const toggleDataSaver = useCallback(() => {
    setDataSaver(!dataSaver);
  }, [dataSaver, setDataSaver]);

  // ====================================
  // 🚀 초기 설정 적용
  // ====================================
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    logger.debug('[AppSettings] 초기화 시작...');
    
    // CSS 변수 업데이트
    updateCSSVariables(fontSize);
    
    // 데이터 절약 모드 클래스 적용
    if (typeof document !== 'undefined') {
      if (dataSaver) {
        document.body.classList.add('data-saver-mode');
        document.documentElement.setAttribute('data-saver', 'true');
      } else {
        document.documentElement.setAttribute('data-saver', 'false');
      }
    }
    
    logger.debug('[AppSettings] 초기 설정 적용 완료:', { fontSize, dataSaver });
  }, [fontSize, dataSaver]);

  // ====================================
  // 🔄 설정 초기화
  // ====================================
  const resetSettings = useCallback(() => {
    setFontSize(FontSizes.MEDIUM);
    setDataSaver(false);
    logger.debug('[AppSettings] 설정 초기화 완료');
  }, [setFontSize, setDataSaver]);

  // ====================================
  // Context 값
  // ====================================
  const contextValue = {
    // 폰트 크기
    fontSize,
    setFontSize,
    fontSizeConfig: FONT_SIZE_PRESETS[fontSize] || FONT_SIZE_PRESETS[FontSizes.MEDIUM],
    fontScale: FONT_SCALE[fontSize] ?? 1.0,
    FontSizes,
    FONT_SIZE_PRESETS,
    
    // 데이터 절약 모드
    dataSaver,
    setDataSaver,
    toggleDataSaver,
    isDataSaverEnabled: dataSaver,
    
    // 유틸리티
    resetSettings,
    
    // 이미지 품질 헬퍼
    getImageQuality: () => dataSaver ? 'low' : 'high',
    shouldUseThumbnail: () => dataSaver,
  };

  return (
    <AppSettingsContext.Provider value={contextValue}>
      {children}
    </AppSettingsContext.Provider>
  );
};

// ====================================
// useAppSettings Hook
// ====================================
export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  
  return context;
};

// ====================================
// 🔧 헬퍼 함수들 (Context 외부에서도 사용 가능)
// ====================================

/**
 * 데이터 절약 모드 여부 확인
 */
export const isDataSaverEnabled = () => {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEYS.DATA_SAVER) === 'true';
};

/**
 * 현재 폰트 크기 가져오기
 */
export const getCurrentFontSize = () => {
  if (typeof localStorage === 'undefined') return FontSizes.MEDIUM;
  const saved = localStorage.getItem(STORAGE_KEYS.FONT_SIZE);
  return saved && Object.values(FontSizes).includes(saved) ? saved : FontSizes.MEDIUM;
};

/**
 * 이미지 품질 결정
 */
export const getImageQuality = () => {
  return isDataSaverEnabled() ? 'low' : 'high';
};

/**
 * 폰트 스케일 값 가져오기
 */
export const getFontScale = () => {
  const size = getCurrentFontSize();
  return FONT_SCALE[size] ?? 1.0;
};

// ====================================
// Default Export
// ====================================
export default AppSettingsContext;